import { ref } from 'vue';
import SalesTransactionsAPI from 'dashboard/api/salesTransactions';

export function useSalesActions({ notify, refresh, reloadTransaction, t }) {
  const archiveTarget = ref(null);
  const finalizeTarget = ref(null);
  const busyActions = ref({});
  const archiveSaving = ref(false);
  const finalizeSaving = ref(false);

  const errorMessage = error =>
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.response?.data?.errors?.[0] ||
    t('SALES.ALERTS.ACTION_ERROR');

  const setBusy = (transaction, action, value) => {
    busyActions.value = {
      ...busyActions.value,
      [transaction.id]: value ? action : '',
    };
  };

  const reload = async transaction => {
    await refresh();
    await reloadTransaction(transaction.id);
  };

  const openArchive = transaction => {
    archiveTarget.value = transaction;
  };

  const closeArchive = () => {
    if (!archiveSaving.value) archiveTarget.value = null;
  };

  const confirmArchive = async reason => {
    if (!archiveTarget.value) return;

    const transaction = archiveTarget.value;
    archiveSaving.value = true;
    setBusy(transaction, 'archive', true);
    try {
      await SalesTransactionsAPI.archive(transaction.id, reason);
      notify(t('SALES.ALERTS.ARCHIVE_SUCCESS'));
      await reload(transaction);
      archiveTarget.value = null;
    } catch (error) {
      notify(errorMessage(error));
    } finally {
      archiveSaving.value = false;
      setBusy(transaction, 'archive', false);
    }
  };

  const restore = async transaction => {
    setBusy(transaction, 'restore', true);
    try {
      await SalesTransactionsAPI.restore(transaction.id);
      notify(t('SALES.ALERTS.RESTORE_SUCCESS'));
      await reload(transaction);
    } catch (error) {
      notify(errorMessage(error));
    } finally {
      setBusy(transaction, 'restore', false);
    }
  };

  const openFinalize = transaction => {
    finalizeTarget.value = transaction;
  };

  const closeFinalize = () => {
    if (!finalizeSaving.value) finalizeTarget.value = null;
  };

  const confirmFinalize = async () => {
    if (!finalizeTarget.value) return;

    const transaction = finalizeTarget.value;
    finalizeSaving.value = true;
    setBusy(transaction, 'finalize', true);
    try {
      await SalesTransactionsAPI.finalize(transaction.id);
      notify(t('SALES.ALERTS.FINALIZE_SUCCESS'));
      await reload(transaction);
      finalizeTarget.value = null;
    } catch (error) {
      notify(errorMessage(error));
    } finally {
      finalizeSaving.value = false;
      setBusy(transaction, 'finalize', false);
    }
  };

  return {
    archiveSaving,
    archiveTarget,
    busyActions,
    closeArchive,
    closeFinalize,
    confirmArchive,
    confirmFinalize,
    finalizeSaving,
    finalizeTarget,
    openArchive,
    openFinalize,
    restore,
  };
}
